import './AdminsSection.css';
import React, { useState, useEffect, useCallback } from 'react';
import { loadAdminsData, addAdmin, editAdmin, resendEmail, removeAdmin } from '../../logic/adminsLogic';
import { ActIcon } from '../../components/AdminIcons';

export default function AdminsSection({ notify }) {
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [adminForm, setAdminForm] = useState({ email: '', full_name: '', admin_role_id: '' });
  const [adminEditId, setAdminEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const isBusy = key => !!busy[key];
  const withBusy = async (key, fn) => {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } finally { setBusy(b => ({ ...b, [key]: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { admins, roles } = await loadAdminsData();
      setAdmins(admins);
      setRoles(roles);
    } catch (e) { notify(e.response?.data?.message || 'Failed to load admins', true); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const createAdmin = async e => {
    e.preventDefault();
    await withBusy('create-admin', async () => {
      try {
        const result = await addAdmin(adminForm);
        notify(`Admin "${result.username}" created — credentials emailed`);
        setAdminForm({ email: '', full_name: '', admin_role_id: '' });
        load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const updateAdmin = async (id, payload) => {
    await withBusy(`update-admin-${id}`, async () => {
      try {
        await editAdmin(id, payload);
        notify('Admin updated'); setAdminEditId(null); load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const resendAdminEmail = async (id, name) => {
    await withBusy(`resend-admin-${id}`, async () => {
      try {
        await resendEmail(id);
        notify(`New credentials emailed to ${name}`);
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  const deleteAdmin = async (id, name) => {
    if (!window.confirm(`Delete admin "${name}"? This cannot be undone.`)) return;
    await withBusy(`delete-admin-${id}`, async () => {
      try {
        await removeAdmin(id);
        notify(`Admin "${name}" deleted`); load();
      } catch (err) { notify(err.response?.data?.message || 'Failed', true); }
    });
  };

  return (
    <div className="fade-up">
      <div className="create-form-wrap" style={{marginBottom:'2rem'}}>
        <div className="info-banner"><span>👤</span><div>
          <strong>Create Sub-Admin</strong>
          <p>A temporary password will be generated and emailed. The admin must change it on first login.</p>
        </div></div>
        <form onSubmit={createAdmin} className="create-form">
          <div className="form-section"><h3>{adminEditId ? 'Edit Admin' : 'New Admin'}</h3>
            <div className="form-grid">
              <div className="input-group" style={{gridColumn:'1/-1'}}>
                <label>Full Name *</label>
                <input className="input-field" placeholder="Jane Smith"
                  value={adminForm.full_name} onChange={e=>setAdminForm(f=>({...f,full_name:e.target.value}))} required/>
              </div>
              <div className="input-group">
                <label>Email Address *</label>
                <input type="email" className="input-field" placeholder="admin@company.com"
                  value={adminForm.email} onChange={e=>setAdminForm(f=>({...f,email:e.target.value}))} required/>
              </div>
              <div className="input-group">
                <label>Assign Role</label>
                <select className="input-field" value={adminForm.admin_role_id}
                  onChange={e=>setAdminForm(f=>({...f,admin_role_id:e.target.value}))}>
                  <option value="">— No role assigned —</option>
                  {roles.map(r=>(
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                {roles.length===0 && <span style={{fontSize:'0.78rem',color:'var(--muted)'}}>No roles yet — create one in the Roles tab first.</span>}
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={isBusy('create-admin')}>
              {isBusy('create-admin') ? <><span className="btn-spinner"/>Creating…</> : '✚ Create & Send Email'}
            </button>
            <button type="button" className="btn btn-outline" onClick={()=>setAdminForm({email:'',full_name:'',admin_role_id:''})}>Reset</button>
          </div>
        </form>
      </div>

      <div className="results-actions">
        <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}>{loading?'↻ …':'↻ Refresh'}</button>
        <span className="results-count">{admins.length} admins</span>
      </div>
      {loading ? <div className="admin-loading"><div className="spinner"/></div>
      : admins.length===0 ? <div className="empty-state"><span>👤</span><p>No admins yet.</p></div>
      : <div className="results-table-wrap"><table className="results-table">
          <thead><tr><th>Admin</th><th>Username</th><th>Role</th><th>Type</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{admins.map(a=>(
            <tr key={a.id}>
              <td><div className="candidate-cell">
                <div className="candidate-avatar">{(a.username||'?')[0].toUpperCase()}</div>
                <div>
                  <div className="cname">{a.username}</div>
                  <div className="cemail">{a.email}</div>
                  {a.is_temporary_password && <span className="badge badge-gold" style={{fontSize:'0.65rem',marginTop:'2px'}}>Temp Password</span>}
                </div>
              </div></td>
              <td><code style={{color:'var(--accent2)',fontSize:'0.82rem'}}>{a.username}</code></td>
              <td>
                {adminEditId === a.id ? (
                  <select className="input-field" style={{width:'auto',padding:'0.3rem 0.5rem',fontSize:'0.82rem'}}
                    defaultValue={a.admin_role_id||''}
                    onChange={e=>updateAdmin(a.id, { admin_role_id: e.target.value || null })}>
                    <option value="">— No role —</option>
                    {roles.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                ) : (
                  <span className="badge badge-purple">{a.adminRole?.name || '—'}</span>
                )}
              </td>
              <td><span className={`badge ${a.role==='master_admin'?'badge-gold':'badge-green'}`}>{a.role==='master_admin'?'Master':'Sub-Admin'}</span></td>
              <td><span className={`badge ${a.is_active?'badge-green':'badge-red'}`}>{a.is_active?'Active':'Inactive'}</span></td>
              <td>
                <div className="action-btns">
                  {a.role !== 'master_admin' && (
                    <>
                      <button className="act-btn info" title="Edit role" onClick={()=>setAdminEditId(adminEditId===a.id?null:a.id)}>
                        <ActIcon type="edit"/>
                      </button>
                      <button className="act-btn info" title="Resend credentials" disabled={isBusy(`resend-admin-${a.id}`)} onClick={()=>resendAdminEmail(a.id, a.username)}>
                        {isBusy(`resend-admin-${a.id}`) ? <span className="act-spinner"/> : <ActIcon type="send"/>}
                      </button>
                      <button className="act-btn warn" title={a.is_active?'Deactivate':'Activate'} disabled={isBusy(`update-admin-${a.id}`)} onClick={()=>updateAdmin(a.id,{is_active:!a.is_active})}>
                        {isBusy(`update-admin-${a.id}`) ? <span className="act-spinner"/> : <ActIcon type={a.is_active?'lock':'unlock'}/>}
                      </button>
                      <button className="act-btn danger" title="Delete admin" disabled={isBusy(`delete-admin-${a.id}`)} onClick={()=>deleteAdmin(a.id, a.username)}>
                        {isBusy(`delete-admin-${a.id}`) ? <span className="act-spinner"/> : <ActIcon type="trash"/>}
                      </button>
                    </>
                  )}
                  {a.role === 'master_admin' && <span style={{fontSize:'0.75rem',color:'var(--muted)'}}>Protected</span>}
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table></div>}
    </div>
  );
}
